/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {EntityTreeModel} from '../../../../shared/entity-tree/models/entity-tree.model';
import {BusinessSettingsDisplayConfiguration} from '../../configuration/v1/business-settings-display-configuration';
import {BusinessSettingsDisplayData} from '../../configuration/v1/business-settings-display-data';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {Subscription} from 'rxjs';
import {EntityTypeEnum} from '../../configuration/v1/entity-type.enum';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {BusinessSettingsModel} from '../../../../api/ms-settings/business/schema/v1/contracts/business-settings.model';
import {BusinessSettingsUtil} from '../../services/business-settings-util.service';
import {EntityTreeUpdateModel} from '../../../../shared/entity-tree/entity-tree/entity-tree-update.model';
import {TreeNodeModel} from '../../models/tree-node-model';
import {EntityTreeBranchModel} from '../../../../shared/entity-tree/models/entity-tree-branch.model';
import {EntityTreeBranchTypeEnum} from '../../../../shared/entity-tree/models/entity-tree-branch-type.enum';
import {BusinessSettingsDisplayDataModel} from '../../configuration/v1/models/business-settings-display-data.model';
import {CountryNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/country-node.model';
import {PartnerNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/partner-node.model';
import {ShipToPartyNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/ship-to-party-node.model';
import {SupplyingPackingPlantNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/supplying-packing-plant-node.model';
import {TransportModeNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/transport-mode-node.model';
import {TransportServiceNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/transport-service-node.model';
import {ClaimCodeNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/claim-code-node.model';
import {BusinessSettingsVersionLineDataModel} from '../../models/business-settings-version-line-data.model';
import {EntityTreeDisplayModeEnum} from '../../models/entity-tree-display-mode.enum';
import {AvailableActions} from '../../../../api/ms-settings/business/schema/v1/contracts/available-actions.enum';
import {SettingStatusEnum} from '../../../../api/ms-settings/business/schema/v1/contracts/setting-status.enum';
import {BusinessSettingsCmdModel} from '../../../../api/ms-settings/business/schema/v1/contracts/business-settings-cmd.model';
import {BusinessSettingsModalTypeEnum} from '../../business-settings-main-page/modals/business-settings-modal-type.enum';
import {FileDownloadHelperService} from '../../../../claim/services/file-download/file-download-helper.service';
import {SettingApi} from '../../../../api/ms-settings/setting.api';
import {SettingConfigModel} from '../../../../api/ms-settings/contracts/setting-config.model';
import moment from 'moment';
import {LoadingService} from '../../../../shared/loading/loading-service/loading.service';
import {ToastWrapperService} from '../../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {AlertNotificationService} from '../../../../alert-notification.service';
import {AlertNotificationSeverity} from '../../../../alert-notification-severity.model';
import {UserService} from '../../../../shared/services/user.service';
import {Permission} from '../../../../shared/utils/permission.enum';
import {ConfirmModalComponent} from '../../../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalConfigurationModel} from '../../../../shared/confirm-modal/confirm-modal/models/confirm-modal-configuration.model';
import {AppService} from '../../../../app.service';
import {filter, take} from 'rxjs/operators';
import {fastDeepCopy} from '../../../../shared/utils/deep-copy';
import {EntityElementActionsEnum} from '../../../../shared/entity-tree/entity-element/models/entity-element-actions.enum';
import {BusinessSettingsTreeMapperService} from '../mapper/business-settings-tree-mapper.service';
import {PathAlertsModel} from '../../../../shared/path-alerts/models/path-alerts.model';
import {PathTypeEnum} from '../../../../shared/path-alerts/models/path-type.enum';
import {PathAlertsEntityModel} from '../../../../shared/path-alerts/models/path-alerts-entity.model';
import {EntityCodeTypeModel} from '../../../../shared/entity-tree/models/entity-code-type.model';
import {PathModel} from '../../../../shared/path-alerts/models/path.model';
import {LeadTimeNodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/relation-tree/lead-time-node.model';
import {SpinnerService} from '../../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../../shared/spinner/spinner-context-type.enum';
import {EntityTreeBranchDisplayDataModel} from '../../configuration/v1/models/entity-tree-branch-display-data.model';

@Component({
    selector: 'app-business-settings-tree',
    templateUrl: './business-settings-tree.component.html',
    styleUrls: ['./business-settings-tree.component.css', '../../../../styles/base-edit-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessSettingsTreeComponent implements OnInit, OnDestroy {

    /**
     * The data to display and populate the entity-tree
     */
    @Input() contextData: BusinessSettingsVersionLineDataModel;
    /**
     * Event emitter to emit when tree is saved
     */
    @Output() treeIsSavingEvent = new EventEmitter<boolean>();
    /**
     * Event emitter to emit when tree was successfully saved (i.e. the update request completed without errors)
     */
    @Output() treeSavedEvent = new EventEmitter<void>();
    /**
     * Event emitter to emit when tree is discarded
     */
    @Output() treeIsDiscardingEvent = new EventEmitter<boolean>();
    /**
     * Event emitter to emit when tree is changed
     */
    @Output() treeChangeEvent = new EventEmitter();
    /**
     * Event emitter to emit tree mode is changed
     */
    @Output() toggleTreeToEditModeEvent = new EventEmitter<BusinessSettingsVersionLineDataModel>();

    @Output() showBusinessSettingsReplicateModal = new EventEmitter<BusinessSettingsVersionLineDataModel>();
    @Output() showBusinessSettingsApplyModal = new EventEmitter<BusinessSettingsVersionLineDataModel>();

    @Output() showBusinessSettingsModal = new EventEmitter<BusinessSettingsModalTypeEnum>();
    @Output() updatePathAlertsBreadcrumbs = new EventEmitter<PathAlertsModel[]>();

    @Input() triggerSetPath = new EventEmitter<PathModel>();
    @Input() triggerEditEntity = new EventEmitter<PathAlertsEntityModel>();
    @Input() triggerUpdateBreadcrumbs = new EventEmitter<void>();

    /**
     * The indentation chars to the json exported
     */
    JSON_SPACING_INDENTATION = 4;
    /**
     * Default date format
     */
    DEFAULT_DATE_FORMAT = 'DD-MM-YYYY HH:mm:ss';
    /**
     * The date format of the exported json configuration
     */
    EXPORT_DATE_FORMAT = 'YYYYMMDD_HHmm';

    // Data variables
    public entityList: EntityTreeModel = {} as EntityTreeModel;
    public rootTreeNode: TreeNodeModel;
    public entityListData: BusinessSettingsDisplayDataModel = BusinessSettingsDisplayData;
    // Update variables
    public updatedTree: BusinessSettingsModel;
    public treeWasChanged = false;
    public canApply = false;
    // permission
    public Permission = Permission;
    public setTreePath = new EventEmitter<EntityCodeTypeModel[]>();
    public editEntityElement = new EventEmitter<EntityCodeTypeModel>();
    // Subscriptions variable
    private subscriptions = new Subscription();
    private originalBusinessSettingsData: BusinessSettingsModel;
    private originalBusinessSettingsDisplayConfiguration = BusinessSettingsDisplayConfiguration;
    // setting config
    private settingConfig: SettingConfigModel;

    get businessSettingsStateClass(): string {
        return `business-settings ${this.contextData.displayMode}`;
    }

    get showSettingFooter(): boolean {
        return this.isEditMode
                && this.isEditActionAvailable
                && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_EDIT]);

    }

    get showEditActionButton(): boolean {
        return this.isViewMode
                && this.isEditActionAvailable
                && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_EDIT]);

    }

    get showApplyActionButton(): boolean {
        return this.isViewMode
                && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_SEND])
                && this.contextData.availableActions.includes(AvailableActions.SUBMIT);
    }

    get showReplicateActionButton(): boolean {
        return this.isViewMode
                && this.isReplicateActionAvailable
                && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_EDIT]);

    }

    get isEditMode(): boolean {
        return this.contextData != null && this.contextData.displayMode === EntityTreeDisplayModeEnum.EDIT;
    }

    get isViewMode(): boolean {
        return this.contextData != null && this.contextData.displayMode === EntityTreeDisplayModeEnum.VIEW;
    }

    get isEditActionAvailable(): boolean {
        return this.contextData.availableActions.includes(AvailableActions.EDIT);
    }

    get isSubmitActionAvailable(): boolean {
        return this.contextData.availableActions.includes(AvailableActions.SUBMIT);
    }

    get isReplicateActionAvailable(): boolean {
        return this.contextData.availableActions.includes(AvailableActions.REPLICATE);
    }

    get titleStatusClass(): string {
        return `business-settings-title-status ${this.contextData.displayMode}`;
    }

    constructor(private businessSettingsApi: BusinessSettingsApi,
                private businessSettingsUtil: BusinessSettingsUtil,
                private toastService: ToastWrapperService,
                private loadingService: LoadingService,
                private spinnerService: SpinnerService,
                private fileDownloadService: FileDownloadHelperService,
                private settingsApi: SettingApi,
                private clientErrorService: AlertNotificationService,
                private userService: UserService,
                private modal: NgbModal,
                private appService: AppService,
                private pathsAlerts: BusinessSettingsTreeMapperService) {
        this.entityList.specificBranches = this.originalBusinessSettingsDisplayConfiguration.specificBranches;
        this.entityList.commonBranch = this.originalBusinessSettingsDisplayConfiguration.commonBranch;
        this.entityList.specificBranchOptions = this.originalBusinessSettingsDisplayConfiguration.specificBranchOptions;
    }

    ngOnInit(): void {
        // Save copy of the original business settings data
        this.originalBusinessSettingsData = fastDeepCopy(this.contextData.data);
        // Map the original/initial data
        this.mapBusinessSettingsOriginalData();

        this.subscriptions.add(this.settingsApi.getSettingConfig()
                .subscribe((settingConfig) => {
                    this.settingConfig = settingConfig;
                }));

        this.subscriptions.add(this.triggerEditEntity.subscribe((entity: PathAlertsEntityModel) => {
            this.emitEditEntityElement(entity);
        }));

        this.subscriptions.add(this.triggerSetPath.subscribe((path: PathModel) => {
            this.emitSetTreePath(path);
        }));

        this.subscriptions.add(this.triggerUpdateBreadcrumbs.subscribe(() => {
            this.updatedPathsHandler(this.rootTreeNode);
        }));

    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Emits the component toggle to edit mode
     */
    toggleToEditMode(): void {
        this.toggleTreeToEditModeEvent.emit(this.contextData);
    }

    /**
     * Emits the component toggle to open the replicate modal
     */
    toggleToShowBusinessSettingsReplicateModal(businessSettingsVersionLineData: BusinessSettingsVersionLineDataModel): void {
        this.showBusinessSettingsReplicateModal.emit(businessSettingsVersionLineData);
    }

    /**
     * Emits the component toggle to open the apply modal
     */
    toggleToShowBusinessSettingsApplyModal(businessSettingsVersionLineData: BusinessSettingsVersionLineDataModel): void {
        this.showBusinessSettingsApplyModal.emit(businessSettingsVersionLineData);
    }

    /**
     * Updated the businessSettingsDisplay according to the received updates
     * @param updateDelta the update values
     */
    updateBusinessSettingsDisplay(updateDelta: EntityTreeUpdateModel): void {
        this.treeWasChanged = true;

        this.treeChangeEvent.emit();

        if (this.updatedTree == null) {
            this.updatedTree = {
                relationTrees: {
                    countries: []
                },
                pools: {
                    countries: [],
                    partners: [],
                    shipToParties: [],
                    supplyingPackingPlants: [],
                    transportServices: [],
                    transportModes: [],
                    leadTimes: [],
                    warnings: [],
                    reasons: [],
                    claimCodes: [],
                    decisionCodes: [],
                    errorCodes: [],
                    businessCategories: [],
                    technicianGroups: []
                }
            };
        }

        this.updateBusinessDataPool(updateDelta);
        this.updateRelationTree(updateDelta);
    }

    /**
     * Initiates the discard process, opening the confirm modal and subscribing to its the confirm event
     */
    discard(): void {
        if (this.treeWasChanged) {
            const discardModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {centered: true}).componentInstance;
            discardModalInstance.modalConfiguration = {
                title: 'Confirm',
                primaryActionTitle: 'Keep editing',
                secondaryActionTitle: 'Discard changes',
                alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
                body: {
                    subtitle: 'With this action you will lose unsaved changes. Are you sure you want to discard your changes?'
                }
            };
            discardModalInstance.secondaryEvent.subscribe(() => this.discardChanges());
        } else {
            this.discardChanges();
        }
    }

    /**
     * Saves the changes
     */
    save(): void {
        const confirmModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;

        confirmModalInstance.modalConfiguration = {
            body: {
                width: 420,
                title: 'Are you sure you want to save the draft version?',
                subtitle: 'Would you like to remark something about this draft? Perhaps a message for a colleague or just a reminder?',
                notes: {
                    height: 127,
                    placeholder: 'Please, write a note here…',
                    notesValue: this.contextData.notes,
                    isNotesMandatory: false,
                    maxNumberCharacters: 200
                }
            },
            title: 'Saving draft version',
            primaryActionTitle: 'Save',
            secondaryActionTitle: 'Discard',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };

        confirmModalInstance.primaryEvent.subscribe((notes: string) => {
            this.treeIsSavingEvent.emit(true);

            const businessSettingsData = {
                notes: notes,
                businessSettings: this.updatedTree
            } as BusinessSettingsCmdModel;

            this.businessSettingsApi.updateSettings(this.contextData.version, businessSettingsData)
                    .subscribe(() => {
                        this.toastService.success('The matrices management version was successfully saved.', 'Success');
                        this.treeWasChanged = false;
                        // Only finalize (unlock accordion/reload table) when the save actually succeeded.
                        // On error, the global APIInterceptor shows the error dialog and the edit lock/state is left untouched.
                        this.treeSavedEvent.emit();
                    })
                    .add(() => {
                        this.treeIsSavingEvent.emit(false);
                    });
        });
    }

    /**
     * Exports the current configuration to json file
     */
    exportConfiguration(): void {
        const date: string = moment(this.contextData.lastChangeDateTime, this.DEFAULT_DATE_FORMAT, true)
                .format(this.EXPORT_DATE_FORMAT);
        const version = this.contextData.status.code === SettingStatusEnum.DRAFT
                ? SettingStatusEnum.DRAFT.toLocaleLowerCase() : `v${this.contextData.version}`;
        const fileName: string = this.settingConfig.exportFileTemplate
                .replace('<version>', version)
                .replace('<date>', date);
        this.fileDownloadService.createFileAndDownload(
                fileName, JSON.stringify(this.contextData.data, null, this.JSON_SPACING_INDENTATION));
    }

    onUploadConfiguration(configuration: string): void {
        try {
            const newTree: BusinessSettingsModel = JSON.parse(configuration);
            this.openUploadConfigurationModal(newTree);
        } catch {
            this.clientErrorService.notificationEventEmitter.emit({
                message: 'Invalid JSON file',
                alertNotificationSeverity: AlertNotificationSeverity.ERROR
            });
        }
    }

    /**
     * Given an updated pool and an original pool, returns an array with the original pool items that exist in the updated pool
     * @param updatedPool The updated pool
     * @param originalPool The pool with the original items
     * TODO: This function will have to be updated when the add and edit pool item functionalities are implemented
     */
    updateToBusinessDataPoolElements(updatedPool: any[], originalPool: any[]): any[] {
        // Searches for the items in the original pool and remaps them to the updated one
        return updatedPool.map((item) => originalPool.find((element) => element.code === item.code));
    }

    updatedPathsHandler(updatedPaths: TreeNodeModel): void {
        const pathAlerts = this.pathsAlerts.retrieveBreadcrumbs(updatedPaths, this.entityList);

        this.canApply = !this.hasUnfinishedPaths(pathAlerts);
        this.updateBusinessSettingsPathAlerts(pathAlerts);
    }

    updateBusinessSettingsPathAlerts(pathAlerts: PathAlertsModel[]): void {
        this.updatePathAlertsBreadcrumbs.emit(pathAlerts);
    }

    hasUnfinishedPaths(pathAlerts: PathAlertsModel[]): boolean {
        const unfinishedPathsCount = pathAlerts?.filter((pa) => pa.type.code === PathTypeEnum.UNFINISHED)
                .map((pa) => pa.type.count)
                .reduce((sum, value) => sum + value, 0) ?? 0;

        return unfinishedPathsCount > 0;
    }

    private emitEditEntityElement(entity: PathAlertsEntityModel): void {
        this.editEntityElement.emit({code: entity.paths[0].entity.code, type: entity.paths[0].type});
    }

    private emitSetTreePath(path: PathModel): void {
        const flatPath = this.pathsAlerts.retrievePathCodes(path);
        this.setTreePath.emit(flatPath);
    }

    /**
     * Discards the changes and resets the page to its initial values
     */
    private discardChanges(): void {
        this.treeIsDiscardingEvent.emit(true);
        this.mapBusinessSettingsOriginalData();
        this.treeWasChanged = false;
        this.toastService.success('The matrices management version was successfully discarded.', 'Success');
        this.treeIsDiscardingEvent.emit(false);
    }

    private overrideConfiguration(newTree: BusinessSettingsModel): void {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.appService.emitShowBackgroundBlur(false);

        this.subscriptions.add(this.businessSettingsApi.validateSettings(newTree)
                .subscribe(() => {
                    this.resetBusinessSettingsDisplayConfiguration();
                    this.updatedTree = newTree;
                    this.mapBusinessSettingsValuesToEntityTree(this.updatedTree);
                    this.treeWasChanged = true;
                    this.treeChangeEvent.emit();
                    this.toastService.success('The configuration file was successfully uploaded.', 'Success');
                })
                .add(() => {
                    this.spinnerService.onGlobalChange()
                            .pipe(filter((value) => !value), take(1))
                            .subscribe(() => {
                                this.appService.emitShowBackgroundBlur(true);
                            });
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                }));
    }

    private openUploadConfigurationModal(configuration: BusinessSettingsModel): void {
        const confirmModal: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;
        const modalConfiguration: ConfirmModalConfigurationModel = {
            body: {
                title: 'Are you sure you want to proceed?',
                subtitle: 'With this action you will override the current draft version by the uploaded file configurations.'
            },
            title: 'Confirm',
            primaryActionTitle: 'Proceed',
            secondaryActionTitle: 'Cancel',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };
        confirmModal.modalConfiguration = modalConfiguration;

        confirmModal.primaryEvent.subscribe(() => {
            this.overrideConfiguration(configuration);
        });
    }

    /**
     * Updates a specific pool
     * @param updateDelta The update delta
     * TODO: This function will have to be updated when the add and edit pool item functionalities are implemented or entities added
     */
    private updateBusinessDataPool(updateDelta: EntityTreeUpdateModel): void {

        const countriesBranch = updateDelta.tree.commonBranch.get(EntityTypeEnum.COUNTRIES);
        this.updatedTree.pools.countries = countriesBranch.pool?.map(countriesBranch.mapOriginalValueFunction);

        const partnersBranch = updateDelta.tree.commonBranch.get(EntityTypeEnum.PARTNERS);
        this.updatedTree.pools.partners = partnersBranch.pool?.map(partnersBranch.mapOriginalValueFunction);

        const shipToPartiesBranch = updateDelta.tree.commonBranch.get(EntityTypeEnum.SHIP_TO_PARTIES);
        this.updatedTree.pools.shipToParties = shipToPartiesBranch.pool?.map(shipToPartiesBranch.mapOriginalValueFunction);

        const supplyingPackingPlantsBranch = updateDelta.tree.commonBranch.get(EntityTypeEnum.SUPPLYING_PACKING_PLANTS);
        this.updatedTree.pools.supplyingPackingPlants =
                supplyingPackingPlantsBranch.pool?.map(supplyingPackingPlantsBranch.mapOriginalValueFunction);

        updateDelta.tree.specificBranches.forEach((value, key) => {

            switch (key) {
                case EntityTreeBranchTypeEnum.SPO:
                    const transportModesBranch = value.get(EntityTypeEnum.TRANSPORT_MODES);
                    this.updatedTree.pools.transportModes = transportModesBranch.pool?.map(transportModesBranch.mapOriginalValueFunction);

                    const transportServicesBranch = value.get(EntityTypeEnum.TRANSPORT_SERVICES);
                    this.updatedTree.pools.transportServices =
                            transportServicesBranch.pool?.map(transportServicesBranch.mapOriginalValueFunction);

                    const leadTimesBranch = value.get(EntityTypeEnum.LEAD_TIMES);
                    this.updatedTree.pools.leadTimes = leadTimesBranch.pool?.map(leadTimesBranch.mapOriginalValueFunction);

                    const warningsBranch = value.get(EntityTypeEnum.WARNINGS);
                    this.updatedTree.pools.warnings = warningsBranch.pool?.map(warningsBranch.mapOriginalValueFunction);

                    const reasonsBranch = value.get(EntityTypeEnum.REASONS);
                    this.updatedTree.pools.reasons = reasonsBranch.pool?.map(reasonsBranch.mapOriginalValueFunction);
                    break;

                case EntityTreeBranchTypeEnum.CLAIM:
                    const claimCodesBranch = value.get(EntityTypeEnum.CLAIM_CODES);
                    this.updatedTree.pools.claimCodes = claimCodesBranch.pool?.map(claimCodesBranch.mapOriginalValueFunction);

                    const decisionCodesBranch = value.get(EntityTypeEnum.DECISION_CODES);
                    this.updatedTree.pools.decisionCodes = decisionCodesBranch.pool?.map(decisionCodesBranch.mapOriginalValueFunction);
                    const errorCodesBranch = value.get(EntityTypeEnum.ERROR_CODES);
                    this.updatedTree.pools.errorCodes = errorCodesBranch.pool?.map(errorCodesBranch.mapOriginalValueFunction);

                    const biCategoriesBranch = value.get(EntityTypeEnum.BI_CATEGORIES);
                    this.updatedTree.pools.businessCategories = biCategoriesBranch.pool?.map(biCategoriesBranch.mapOriginalValueFunction);
                    break;

                case EntityTreeBranchTypeEnum.PNC:
                    const technicianGroupsBranch = value.get(EntityTypeEnum.TECHNICIANS_GROUPS);
                    this.updatedTree.pools.technicianGroups = technicianGroupsBranch
                            .pool?.map(technicianGroupsBranch.mapOriginalValueFunction);
                    break;

                default:
                    break;
            }
        });
    }

    /**
     * Updates the relation tree
     * @param updateDelta The update delta
     */
    private updateRelationTree(updateDelta: EntityTreeUpdateModel): void {
        this.updatedTree.relationTrees.countries = [];
        updateDelta.relations.relationTree.forEach((country) => {

            const countryNode: CountryNodeModel = {
                code: country.code
            };
            if (country.relationTree != null) {

                // Partners node
                const partners = this.retrieveTypeFromRelationTree(country, EntityTypeEnum.PARTNERS);

                if (partners.length > 0) {
                    countryNode.partners = [];
                    partners.forEach((partner) => {
                        const partnerNode: PartnerNodeModel = {
                            code: partner.code
                        };
                        if (partner.relationTree != null) {

                            // Ship to parties node
                            const shipToParties = this.retrieveTypeFromRelationTree(partner, EntityTypeEnum.SHIP_TO_PARTIES);
                            if (shipToParties.length > 0) {
                                partnerNode.shipToParties = [];
                                shipToParties.forEach((shipToParty) => {
                                    const shipToPartyNode: ShipToPartyNodeModel = {
                                        code: shipToParty.code,
                                        requiresTechnicianGroups: shipToParty.activeChildren != null
                                                ? shipToParty.activeChildren.includes(EntityTypeEnum.TECHNICIANS_GROUPS)
                                                : false
                                    };

                                    if (shipToParty.relationTree != null) {
                                        if (shipToPartyNode.requiresTechnicianGroups) {
                                            // Technicians group node
                                            shipToPartyNode.technicianGroups = [];
                                            const techniciansGroup = shipToParty.relationTree;
                                            if (techniciansGroup.length > 0) {
                                                techniciansGroup.forEach((item) => {
                                                    shipToPartyNode.technicianGroups.push({code: item.code});
                                                });
                                            }
                                        }
                                    }
                                    partnerNode.shipToParties.push(shipToPartyNode);
                                });
                            }

                            // Supplying packing plants node
                            const supplyingPackingPlants = this.retrieveTypeFromRelationTree(
                                    partner, EntityTypeEnum.SUPPLYING_PACKING_PLANTS);
                            if (supplyingPackingPlants.length > 0) {
                                partnerNode.supplyingPackingPlants = [];
                                supplyingPackingPlants.forEach((plant) => {
                                    const supplyingPackingPlantNode: SupplyingPackingPlantNodeModel = {
                                        code: plant.code,
                                        requiresClaimCodes: plant.activeChildren != null
                                                ? plant.activeChildren.includes(EntityTypeEnum.CLAIM_CODES)
                                                : false,
                                        requiresTransportModes: plant.activeChildren != null
                                                ? plant.activeChildren.includes(EntityTypeEnum.TRANSPORT_MODES)
                                                : false
                                    };
                                    if (plant.relationTree != null) {

                                        if (supplyingPackingPlantNode.requiresTransportModes) {
                                            // Transport modes node
                                            const transportModes = this.retrieveTypeFromRelationTree(plant, EntityTypeEnum.TRANSPORT_MODES);
                                            if (transportModes.length > 0) {
                                                supplyingPackingPlantNode.transportModes = [];
                                                transportModes.forEach((mode) => {
                                                    const transportModeNode = this.convertTreeNodeToTransportModeNode(mode);
                                                    supplyingPackingPlantNode.transportModes.push(transportModeNode);
                                                });
                                            }
                                        }

                                        if (supplyingPackingPlantNode.requiresClaimCodes) {
                                            // Claim codes node
                                            const claimCodes = this.retrieveTypeFromRelationTree(plant, EntityTypeEnum.CLAIM_CODES);
                                            if (claimCodes.length > 0) {
                                                supplyingPackingPlantNode.claimCodes = [];
                                                claimCodes.forEach((claimCode) => {
                                                    const claimCodeNode = this.convertTreeNodeToClaimCodeNode(claimCode);
                                                    supplyingPackingPlantNode.claimCodes.push(claimCodeNode);
                                                });
                                            }
                                        }
                                    }
                                    partnerNode.supplyingPackingPlants.push(supplyingPackingPlantNode);
                                });
                            }
                        }
                        countryNode.partners.push(partnerNode);
                    });
                }
            }
            this.updatedTree.relationTrees.countries.push(countryNode);
        });
    }

    /**
     * Converts a {@link TreeNodeModel} in to a {@link ClaimCodeNodeModel} and returns it
     * @param claimCode the {@link TreeNodeModel} to convert
     * @return the converted {@link ClaimCodeNodeModel}
     */
    private convertTreeNodeToClaimCodeNode(claimCode: TreeNodeModel): ClaimCodeNodeModel {
        const claimCodeNode: ClaimCodeNodeModel = {
            code: claimCode.code
        };
        if (claimCode.relationTree != null) {

            // Decision codes node
            const decisionCodes = this.retrieveTypeFromRelationTree(
                    claimCode, EntityTypeEnum.DECISION_CODES);
            if (decisionCodes.length > 0) {
                claimCodeNode.decisionCodes = [];
                decisionCodes.forEach((decisionCode) => {
                    claimCodeNode.decisionCodes.push({code: decisionCode.code});
                });
            }
        }
        return claimCodeNode;
    }

    /**
     * Converts a {@link TreeNodeModel} in to a {@link TransportModeNodeModel} and returns it
     * @param mode the {@link TreeNodeModel} to convert
     * @return the converted {@link TransportModeNodeModel}
     */
    private convertTreeNodeToTransportModeNode(mode: TreeNodeModel): TransportModeNodeModel {
        const transportModeNode: TransportModeNodeModel = {
            code: mode.code,
            requiresTransportServices: mode.activeChildren != null ? mode.activeChildren.includes(EntityTypeEnum.TRANSPORT_SERVICES) : false
        };
        if (mode.relationTree != null) {

            // Transport services OR Lead time from transport mode node
            const transportServices = this.retrieveTypeFromRelationTree(
                    mode, EntityTypeEnum.TRANSPORT_SERVICES);
            const modeLeadTime = this.retrieveElementsTreeNodeFromRelationTreeNode(
                    mode, EntityTypeEnum.LEAD_TIMES);

            // Transport services node
            if (transportModeNode.requiresTransportServices) {
                if (transportServices.length > 0 && modeLeadTime == null) {
                    transportModeNode.transportServices = [];
                    transportServices.forEach((service) => {
                        const transportServiceNode: TransportServiceNodeModel = {
                            code: service.code
                        };
                        if (service.relationTree != null) {

                            // Lead time from transport service node
                            const serviceLeadTime =
                                    this.retrieveElementsTreeNodeFromRelationTreeNode(
                                            service, EntityTypeEnum.LEAD_TIMES);
                            if (serviceLeadTime != null) {
                                transportServiceNode.leadTime = {
                                    code: serviceLeadTime.code,
                                    requiresWarning: serviceLeadTime.activeChildren != null
                                            ? serviceLeadTime.activeChildren.includes(EntityTypeEnum.WARNINGS)
                                            : false
                                };

                                const serviceWarning =
                                        this.retrieveElementsTreeNodeFromRelationTreeNode(
                                                serviceLeadTime, EntityTypeEnum.WARNINGS);
                                if (serviceWarning != null) {
                                    transportServiceNode.leadTime.warning = {code: serviceWarning.code};
                                }
                            }
                        }
                        transportModeNode.transportServices.push(transportServiceNode);
                    });
                }
                // Lead time from transport mode node
            } else if (modeLeadTime != null && transportServices.length === 0) {
                transportModeNode.leadTime = {
                    code: modeLeadTime.code,
                    requiresWarning: modeLeadTime.activeChildren != null
                            ? modeLeadTime.activeChildren.includes(EntityTypeEnum.WARNINGS)
                            : false
                };
                if (transportModeNode.leadTime.requiresWarning) {
                    const serviceWarning =
                            this.retrieveElementsTreeNodeFromRelationTreeNode(
                                    modeLeadTime, EntityTypeEnum.WARNINGS);
                    if (serviceWarning != null) {
                        transportModeNode.leadTime.warning = {code: serviceWarning.code};
                    }
                }
            }
        }
        return transportModeNode;
    }

    /**
     * Resets the BusinessSettingsDisplayConfiguration path, connections and pool values
     */
    private resetBusinessSettingsDisplayConfiguration(): void {
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.forEach((value) => {
            value.connections = [];
            value.pool = [];
            value.path = null;
        });
        if (this.originalBusinessSettingsDisplayConfiguration.specificBranches != null) {
            this.originalBusinessSettingsDisplayConfiguration.specificBranches.forEach((branch) => {
                branch.forEach((value) => {
                    value.connections = [];
                    value.pool = [];
                    value.path = null;
                });
            });
        }
    }

    /**
     * Maps the {@link BusinessSettingsModel} values the corresponding {@link TreeNodeModel} and {@link EntityTreeModel}
     * @param businessSettings the retrieved business settings
     * TODO: This function will have to be updated when new entities are added
     */
    private mapBusinessSettingsValuesToEntityTree(businessSettings: BusinessSettingsModel): void {
        this.rootTreeNode = this.mapBusinessSettingsDataToTreeNode(businessSettings);
        this.mapCountryEntityValues(businessSettings);
        this.mapPartnerEntityValues(businessSettings);
        this.mapShipToPartyEntityValues(businessSettings);
        this.mapSupplyingPackingPlantEntityValues(businessSettings);
        this.mapTransportModeEntityValues(businessSettings);
        this.mapTransportServiceEntityValues(businessSettings);
        this.mapLeadTimeEntityValues(businessSettings);
        this.mapWarningEntityValues(businessSettings);
        this.mapReasonEntityValues(businessSettings);
        this.mapClaimCodeEntityValues(businessSettings);
        this.mapDecisionCodeEntityValues(businessSettings);
        this.mapErrorCodeEntityValues(businessSettings);
        this.mapBiCategoryEntityValues(businessSettings);
        this.mapTechniciansGroupEntityValues(businessSettings);

        // Set the pool items parent codes
        this.mapPoolItemsParentCodes(businessSettings);
        // Sort pools by parentCode
        this.sortPoolByParentCode(EntityTypeEnum.PARTNERS);
        this.sortPoolByParentCode(EntityTypeEnum.SHIP_TO_PARTIES);
    }

    /**
     * This method is responsible to map the business settings model into a generic tree node model.
     * @param businessSettings the business settings model.
     * @return the tree node model that represents the business settings model.
     */
    private mapBusinessSettingsDataToTreeNode(businessSettings: BusinessSettingsModel): TreeNodeModel {

        const root: TreeNodeModel = {
            relationTree: []
        };

        if (businessSettings != null && businessSettings.relationTrees != null) {

            if (businessSettings.relationTrees.countries != null) {

                businessSettings.relationTrees.countries.forEach((country) => {
                    const countryNode: TreeNodeModel = {
                        code: country.code,
                        relationTree: [],
                        type: EntityTypeEnum.COUNTRIES,
                        activeChildren: [EntityTypeEnum.PARTNERS]
                    };
                    if (country.partners != null) {
                        country.partners.forEach((partner) => {

                            const partnerNode: TreeNodeModel = {
                                code: partner.code,
                                relationTree: [],
                                type: EntityTypeEnum.PARTNERS,
                                activeChildren: [EntityTypeEnum.SHIP_TO_PARTIES, EntityTypeEnum.SUPPLYING_PACKING_PLANTS]
                            };
                            if (partner.shipToParties != null) {
                                partner.shipToParties.forEach((shipToParty) => {
                                    const shipToPartyNode: TreeNodeModel = {
                                        code: shipToParty.code,
                                        type: EntityTypeEnum.SHIP_TO_PARTIES,
                                        relationTree: [],
                                        activeChildren: this.retrieveShipToPartyActiveChildren(shipToParty)
                                    };

                                    if (shipToParty.technicianGroups != null) {
                                        shipToParty.technicianGroups.forEach((item) => {
                                            const technicianGroupsNode: TreeNodeModel = {
                                                code: item.code,
                                                type: EntityTypeEnum.TECHNICIANS_GROUPS
                                            };
                                            shipToPartyNode.relationTree.push(technicianGroupsNode);
                                        });
                                    }
                                    partnerNode.relationTree.push(shipToPartyNode);
                                });
                            }

                            if (partner.supplyingPackingPlants != null) {
                                partner.supplyingPackingPlants.forEach((plant) => {
                                    const plantNode: TreeNodeModel = {
                                        code: plant.code,
                                        relationTree: [],
                                        type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS,
                                        activeChildren: this.retrieveSupplyingPackingPlantActiveChildren(plant)
                                    };

                                    if (plant.transportModes != null) {
                                        plant.transportModes.forEach((mode) => {
                                            const transportModeNode: TreeNodeModel = {
                                                code: mode.code,
                                                relationTree: [],
                                                type: EntityTypeEnum.TRANSPORT_MODES,
                                                activeChildren: this.retrieveTransportModeActiveChildren(mode)
                                            };

                                            if (mode.transportServices != null) {
                                                mode.transportServices.forEach((service) => {
                                                    const transportServiceNode: TreeNodeModel = {
                                                        code: service.code,
                                                        relationTree: [],
                                                        type: EntityTypeEnum.TRANSPORT_SERVICES,
                                                        activeChildren: this.retrieveTransportServiceActiveChildren(mode)
                                                    };

                                                    if (service.leadTime != null) {
                                                        const leadTimeNode: TreeNodeModel = {
                                                            code: service.leadTime.code,
                                                            type: EntityTypeEnum.LEAD_TIMES,
                                                            relationTree: [],
                                                            activeChildren: this.retrieveLeadTimeActiveChildren(service.leadTime)
                                                        };

                                                        if (service.leadTime.warning != null) {
                                                            const warningNode: TreeNodeModel = {
                                                                code: service.leadTime.warning.code,
                                                                type: EntityTypeEnum.WARNINGS
                                                            };
                                                            leadTimeNode.relationTree.push(warningNode);
                                                        }
                                                        transportServiceNode.relationTree.push(leadTimeNode);
                                                    }
                                                    transportModeNode.relationTree.push(transportServiceNode);
                                                });
                                            } else if (mode.leadTime != null) {
                                                const leadTimeNode: TreeNodeModel = {
                                                    code: mode.leadTime.code,
                                                    type: EntityTypeEnum.LEAD_TIMES,
                                                    relationTree: [],
                                                    activeChildren: this.retrieveLeadTimeActiveChildren(mode.leadTime)
                                                };

                                                if (mode.leadTime.warning != null) {
                                                    const warningNode: TreeNodeModel = {
                                                        code: mode.leadTime.warning.code,
                                                        type: EntityTypeEnum.WARNINGS
                                                    };
                                                    leadTimeNode.relationTree.push(warningNode);
                                                }
                                                transportModeNode.relationTree.push(leadTimeNode);
                                            }

                                            plantNode.relationTree.push(transportModeNode);
                                        });
                                    }

                                    if (plant.claimCodes != null) {
                                        plant.claimCodes.forEach((claimCode) => {
                                            const claimCodeNode: TreeNodeModel = {
                                                code: claimCode.code,
                                                relationTree: [],
                                                type: EntityTypeEnum.CLAIM_CODES,
                                                activeChildren: [EntityTypeEnum.DECISION_CODES]
                                            };

                                            if (claimCode.decisionCodes != null) {
                                                claimCode.decisionCodes.forEach((decisionCode) => {
                                                    const decisionCodeNode: TreeNodeModel = {
                                                        code: decisionCode.code,
                                                        type: EntityTypeEnum.DECISION_CODES,
                                                        relationTree: []
                                                    };
                                                    claimCodeNode.relationTree.push(decisionCodeNode);
                                                });
                                            }
                                            plantNode.relationTree.push(claimCodeNode);
                                        });
                                    }
                                    partnerNode.relationTree.push(plantNode);
                                });
                            }
                            countryNode.relationTree.push(partnerNode);
                        });
                    }
                    root.relationTree.push(countryNode);
                });
            }
        }
        return root;
    }

    private retrieveSupplyingPackingPlantActiveChildren(plant: SupplyingPackingPlantNodeModel): EntityTypeEnum[] {
        const activeChildren: EntityTypeEnum[] = [];

        if (plant.requiresTransportModes) {
            activeChildren.push(EntityTypeEnum.TRANSPORT_MODES);
        }

        if (plant.requiresClaimCodes) {
            activeChildren.push(EntityTypeEnum.CLAIM_CODES);
        }

        return activeChildren;
    }

    private retrieveTransportModeActiveChildren(transportMode: TransportModeNodeModel): EntityTypeEnum[] {
        const activeChildren: EntityTypeEnum[] = [];

        if (transportMode.requiresTransportServices) {
            activeChildren.push(EntityTypeEnum.TRANSPORT_SERVICES);
        } else {
            activeChildren.push(EntityTypeEnum.LEAD_TIMES);
        }

        return activeChildren;
    }

    private retrieveTransportServiceActiveChildren(transportMode: TransportModeNodeModel): EntityTypeEnum[] {
        const activeChildren: EntityTypeEnum[] = [];

        if (transportMode.requiresTransportServices) {
            activeChildren.push(EntityTypeEnum.LEAD_TIMES);
        }

        return activeChildren;
    }

    private retrieveLeadTimeActiveChildren(leadTime: LeadTimeNodeModel): EntityTypeEnum[] {
        const activeChildren: EntityTypeEnum[] = [];

        if (leadTime.requiresWarning) {
            activeChildren.push(EntityTypeEnum.WARNINGS);
        }

        return activeChildren;
    }

    private retrieveShipToPartyActiveChildren(shipToParty: ShipToPartyNodeModel): EntityTypeEnum[] {
        const activeChildren: EntityTypeEnum[] = [];

        if (shipToParty.requiresTechnicianGroups) {
            activeChildren.push(EntityTypeEnum.TECHNICIANS_GROUPS);
        }

        return activeChildren;
    }

    /**
     * Resets the displayConfiguration to its initial state and maps the original business settings data retrieved from the backend
     */
    private mapBusinessSettingsOriginalData(): void {
        this.resetBusinessSettingsDisplayConfiguration();
        this.contextData.data = fastDeepCopy(this.originalBusinessSettingsData);
        this.mapBusinessSettingsValuesToEntityTree(this.contextData.data);
    }

    /**
     * Maps the {@link BusinessSettingsModel} country values to the {@link BusinessSettingsDisplayConfiguration} (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapCountryEntityValues(businessData: BusinessSettingsModel): void {
        // Set the country values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.set(EntityTypeEnum.COUNTRIES,
                {
                    ...this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.COUNTRIES),
                    pool: this.mapEntityArraySorting(businessData.pools.countries,
                            this.businessSettingsUtil.mapToEntityElementModel,
                            this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.COUNTRIES),
                            this.entityListData.entitiesData.get(EntityTypeEnum.COUNTRIES))
                });
    }

    /**
     * Maps the {@link BusinessSettingsModel} partner values to the {@link BusinessSettingsDisplayConfiguration} (this.entityList) variable
     * Calls {@link mapPoolItemsParentCodes} to set each partner parent code
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapPartnerEntityValues(businessData: BusinessSettingsModel): void {
        // Set the partners values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.set(EntityTypeEnum.PARTNERS,
                {
                    ...this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.PARTNERS),
                    pool: this.mapEntityArraySorting(businessData.pools.partners,
                            this.businessSettingsUtil.mapToEntityElementModel,
                            this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.PARTNERS),
                            this.entityListData.entitiesData.get(EntityTypeEnum.PARTNERS),
                            true)
                });
    }

    /**
     * Maps the {@link BusinessSettingsModel} ship to party values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapShipToPartyEntityValues(businessData: BusinessSettingsModel): void {
        // Set the ship to parties values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.set(EntityTypeEnum.SHIP_TO_PARTIES,
                {
                    ...this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.SHIP_TO_PARTIES),
                    pool: this.mapEntityArraySorting(businessData.pools.shipToParties,
                            this.businessSettingsUtil.mapToEntityElementModel,
                            this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.SHIP_TO_PARTIES),
                            this.entityListData.entitiesData.get(EntityTypeEnum.SHIP_TO_PARTIES),
                            true)
                });
    }

    /**
     * Maps the {@link BusinessSettingsModel} supplying packing plants values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapSupplyingPackingPlantEntityValues(businessData: BusinessSettingsModel): void {
        // Set the supplying packing plant values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.set(EntityTypeEnum.SUPPLYING_PACKING_PLANTS,
                {
                    ...this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.SUPPLYING_PACKING_PLANTS),
                    pool: this.mapEntityArraySorting(businessData.pools.supplyingPackingPlants,
                            this.businessSettingsUtil.mapToEntityElementModel,
                            this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(EntityTypeEnum.SUPPLYING_PACKING_PLANTS),
                            this.entityListData.entitiesData.get(EntityTypeEnum.SUPPLYING_PACKING_PLANTS))
                });
    }

    /**
     * Maps the {@link BusinessSettingsModel} transport modes values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapTransportModeEntityValues(businessData: BusinessSettingsModel): void {
        // Set the transport mode values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                .set(EntityTypeEnum.TRANSPORT_MODES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                    .get(EntityTypeEnum.TRANSPORT_MODES),
                            pool: this.mapEntityArraySorting(businessData.pools.transportModes,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                            .get(EntityTypeEnum.TRANSPORT_MODES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.TRANSPORT_MODES))
                        });
    }

    /**
     * Maps the {@link BusinessSettingsModel} transport modes values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapTransportServiceEntityValues(businessData: BusinessSettingsModel): void {
        // Set the transport service values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                .set(EntityTypeEnum.TRANSPORT_SERVICES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                    .get(EntityTypeEnum.TRANSPORT_SERVICES),
                            pool: this.mapEntityArraySorting(businessData.pools.transportServices,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                            .get(EntityTypeEnum.TRANSPORT_SERVICES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.TRANSPORT_SERVICES))
                        });
    }

    /**
     * Maps the {@link BusinessSettingsModel} lead times values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapLeadTimeEntityValues(businessData: BusinessSettingsModel): void {
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                .set(EntityTypeEnum.LEAD_TIMES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                    .get(EntityTypeEnum.LEAD_TIMES),
                            pool: this.mapEntityArraySorting(businessData.pools.leadTimes,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                            .get(EntityTypeEnum.LEAD_TIMES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.LEAD_TIMES))
                        });
    }

    /**
     * Maps the {@link BusinessSettingsModel} warnings values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapWarningEntityValues(businessData: BusinessSettingsModel): void {
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                .set(EntityTypeEnum.WARNINGS,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                    .get(EntityTypeEnum.WARNINGS),
                            pool: this.mapEntityArraySorting(businessData.pools.warnings,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                            .get(EntityTypeEnum.WARNINGS),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.WARNINGS))
                        });
    }

    /**
     * Maps the {@link BusinessSettingsModel} reasons values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapReasonEntityValues(businessData: BusinessSettingsModel): void {
        // Set the reasons values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                .set(EntityTypeEnum.REASONS,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                    .get(EntityTypeEnum.REASONS),
                            pool: this.mapEntityArraySorting(businessData.pools.reasons,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.SPO)
                                            .get(EntityTypeEnum.REASONS),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.REASONS))
                        });
    }

    /**
     * Maps the {@link BusinessSettingsModel} claim code values to the {@link BusinessSettingsDisplayConfiguration}
     * (this.entityList) variable
     * @param businessData the {@link BusinessSettingsModel} value to map
     * @return void
     */
    private mapClaimCodeEntityValues(businessData: BusinessSettingsModel): void {
        // Set the ship to parties values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                .set(EntityTypeEnum.CLAIM_CODES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                    .get(EntityTypeEnum.CLAIM_CODES),
                            pool: this.mapEntityArraySorting(businessData.pools.claimCodes,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                            .get(EntityTypeEnum.CLAIM_CODES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.CLAIM_CODES))
                        });
    }

    private mapDecisionCodeEntityValues(businessData: BusinessSettingsModel): void {
        // Set the decision code values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                .set(EntityTypeEnum.DECISION_CODES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                    .get(EntityTypeEnum.DECISION_CODES),
                            pool: this.mapEntityArraySorting(businessData.pools.decisionCodes,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                            .get(EntityTypeEnum.DECISION_CODES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.DECISION_CODES))
                        });
    }

    private mapErrorCodeEntityValues(businessData: BusinessSettingsModel): void {
        // Set the ship to parties values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                .set(EntityTypeEnum.ERROR_CODES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                    .get(EntityTypeEnum.ERROR_CODES),
                            pool: this.mapEntityArraySorting(businessData.pools.errorCodes,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                            .get(EntityTypeEnum.ERROR_CODES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.ERROR_CODES))
                        });
    }

    private mapBiCategoryEntityValues(businessData: BusinessSettingsModel): void {
        // Set the bi categories values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                .set(EntityTypeEnum.BI_CATEGORIES,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                    .get(EntityTypeEnum.BI_CATEGORIES),
                            pool: this.mapEntityArraySorting(businessData.pools.businessCategories,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.CLAIM)
                                            .get(EntityTypeEnum.BI_CATEGORIES),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.BI_CATEGORIES))
                        });
    }

    private mapTechniciansGroupEntityValues(businessData: BusinessSettingsModel): void {
        // Set technicians-groups  values to the FE display configuration
        this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.PNC)
                .set(EntityTypeEnum.TECHNICIANS_GROUPS,
                        {
                            ...this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.PNC)
                                    .get(EntityTypeEnum.TECHNICIANS_GROUPS),
                            pool: this.mapEntityArraySorting(businessData.pools.technicianGroups,
                                    this.businessSettingsUtil.mapToEntityElementModel,
                                    this.originalBusinessSettingsDisplayConfiguration.specificBranches.get(EntityTreeBranchTypeEnum.PNC)
                                            .get(EntityTypeEnum.TECHNICIANS_GROUPS),
                                    this.entityListData.entitiesData.get(EntityTypeEnum.TECHNICIANS_GROUPS))
                        });

    }

    /**
     * Maps the parent information to the pool items
     */
    private mapPoolItemsParentCodes(businessData: BusinessSettingsModel): void {
        if (businessData != null && businessData.relationTrees != null && businessData.relationTrees.countries != null) {
            businessData.relationTrees.countries.forEach((country) => {

                if (country.partners != null) {
                    country.partners.forEach((partner) => {
                        const parentCountry = businessData.pools.countries.find((item) => item.code === country.code);

                        const entityListPartner = this.entityList.commonBranch.get(EntityTypeEnum.PARTNERS)
                                .pool.find((item) => item.code === partner.code);
                        if (entityListPartner != null) {
                            entityListPartner.displayParentCode = parentCountry.alpha3Code;
                            entityListPartner.parentCode = parentCountry.code;
                            entityListPartner.connectedWithParent = true;
                            entityListPartner.childrenCode = parentCountry.alpha3Code;
                        }

                        if (partner.shipToParties != null) {
                            partner.shipToParties.forEach((shipToParty) => {
                                const entityListShipToParty = this.entityList.commonBranch.get(EntityTypeEnum.SHIP_TO_PARTIES)
                                        .pool.find((item) => item.code === shipToParty.code);
                                if (entityListShipToParty != null) {
                                    entityListShipToParty.displayParentCode = parentCountry.alpha3Code;
                                    entityListShipToParty.parentCode = partner.code;
                                    entityListShipToParty.connectedWithParent = true;
                                }
                            });
                        }
                    });
                }
            });
        }
    }

    /**
     * Maps objects array to {@link EntityElementModel[]} sorting it.
     * @param array the objects array to map
     * @param mapFunction a function that converts the array objects to an {@link EntityElementModel}
     * @param configuration the configuration with mapping specifications
     * @param sortByParentCode if the array elements should be sorted by the parent code instead of their own code
     * @return EntityElementModel[] or null if the initial array is null.
     */
    private mapEntityArraySorting(array: any[],
                                  mapFunction: (element: any,
                                                configuration: EntityTreeBranchModel,
                                                data: EntityTreeBranchDisplayDataModel,
                                                active?: EntityElementActionsEnum,
                                                sortByParentCode?: boolean) => EntityElementModel,
                                  configuration: EntityTreeBranchModel,
                                  data: EntityTreeBranchDisplayDataModel,
                                  sortByParentCode?: boolean): EntityElementModel[] {
        let items: EntityElementModel[] = [];
        if (array != null && configuration != null) {
            // If configuration.hasManualSorting = true, return the array in the original order
            if (configuration.hasManualSorting) {
                items = array.map((item) => mapFunction(item, configuration, data, EntityElementActionsEnum.EDIT));
            } else {
                if (sortByParentCode) {
                    items = array.map((item) => mapFunction(item, configuration, data,
                            EntityElementActionsEnum.EDIT, sortByParentCode));
                } else {
                    items = this.businessSettingsUtil.sortEntityElementBySortingCode(array.map((item) =>
                            mapFunction(item, configuration, data)
                    ));
                }
            }
        }
        return items;
    }

    /**
     * Retrieves the leadTime {@link TreeNodeModel} value from a given {@link TreeNodeModel.relationTree}
     * @param node the {@link TreeNodeModel} from which to retrieve the leadTime value from
     */
    private retrieveElementsTreeNodeFromRelationTreeNode(node: TreeNodeModel, entityName: string): TreeNodeModel {
        return node.relationTree != null && node.relationTree.length === 1
        && node.relationTree[0].type === entityName ? node.relationTree[0] : null;
    }

    /**
     * Retrieves a filtered array of a specific {@link EntityTypeEnum} from a given {@link TreeNodeModel}
     * @param tree the {@link TreeNodeModel} from which to retrieve the array
     * @param type the {@link EntityTypeEnum} to filter
     */
    private retrieveTypeFromRelationTree(tree: TreeNodeModel, type: EntityTypeEnum): TreeNodeModel[] {
        return tree.relationTree.filter((item) => item.type === type);
    }

    /**
     * Retrieves a sort array by display Parent code of a specific {@link EntityTypeEnum}
     */
    private sortPoolByParentCode(entityElement: EntityTypeEnum): void {
        this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(entityElement).pool?.forEach((item) => {
            if (item.displayParentCode == null) {
                item.sortingCode = `000 ${item.name}`;
            } else {
                item.sortingCode = `${item.displayParentCode} ${item.name}`;
            }
            return item.sortingCode;
        });

        const poolEntitySort = this.businessSettingsUtil.sortEntityElementBySortingCode
        (this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(entityElement).pool);

        this.originalBusinessSettingsDisplayConfiguration.commonBranch.set(entityElement,
                {
                    ...this.originalBusinessSettingsDisplayConfiguration.commonBranch.get(entityElement),
                    pool: poolEntitySort
                });
    }
}
